import React, { useMemo } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import MonitorHeartOutlinedIcon from '@mui/icons-material/MonitorHeartOutlined';
import { formatVitalsForMonitor } from '../utils/vitalsDisplay';

const VITAL_ITEMS = [
  { key: 'hr', label: 'HR', unit: 'bpm' },
  { key: 'bp', label: 'BP', unit: 'mmHg' },
  { key: 'spo2', label: 'SpO₂', unit: '%' },
  { key: 'rr', label: 'RR', unit: '/min' },
  { key: 'temp', label: 'Temp', unitKey: 'tempUnit' },
];

function VitalRow({ label, value, unit }) {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        gap: 1,
        py: 0.75,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-child': { borderBottom: 'none', pb: 0 },
      }}
    >
      <Typography variant="body2" color="text.secondary" fontWeight={500}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        fontWeight={600}
        sx={{ fontFamily: '"Roboto Mono", "Consolas", monospace', textAlign: 'right' }}
      >
        {value}
        {unit ? (
          <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
            {unit}
          </Typography>
        ) : null}
      </Typography>
    </Box>
  );
}

/**
 * Vital signs card for the simulation sidebar (matches other sidebar panels).
 * Renders when `vitals` is a valid session vitals object.
 */
const VitalSignMonitor = ({ vitals, celcius = false }) => {
  const formatted = useMemo(
    () => formatVitalsForMonitor(vitals, { celcius }),
    [vitals, celcius]
  );

  if (!formatted) return null;

  return (
    <Paper elevation={2} sx={{ p: 2 }}>
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <MonitorHeartOutlinedIcon color="primary" />
        <Typography variant="h6" color="primary.main" fontWeight={600}>
          Vital Signs
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Current patient vitals from the monitor
      </Typography>
      <Box>
        {VITAL_ITEMS.map(({ key, label, unit, unitKey }) => (
          <VitalRow
            key={key}
            label={label}
            value={formatted[key]}
            unit={unitKey ? formatted[unitKey] : unit}
          />
        ))}
      </Box>
    </Paper>
  );
};

export default VitalSignMonitor;
